// PATH: src/components/catalog/StagePanel.tsx
import * as React from "react";
import { Button, Paper, Stack, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/EditOutlined";
import { useGradesApi, useStagesApi } from "../../api";
import type { Grade } from "../../models/domain";
import { cascadeLines, plural, stageCounts, type StageEntry } from "./catalogModel";
import { useCatalog } from "./CatalogContext";
import { SimpleEntityDialog } from "./forms/SimpleEntityDialog";
import { DetailHeader, Facts, ListRow, Rank, Section, SectionEmpty } from "./parts";
import { useCatalogDelete } from "./useCatalogDelete";
import { useDialogState } from "./useDialogState";

type DialogState = { type: "stage" } | { type: "grade"; grade: Grade | null };

export const StagePanel: React.FC<{ entry: StageEntry }> = ({ entry }) => {
  const { index, select } = useCatalog();
  const { updateStage, deleteStage } = useStagesApi();
  const { createGrade, updateGrade, deleteGrade } = useGradesApi();
  const remove = useCatalogDelete();
  const dlg = useDialogState<DialogState>();

  const program = entry.program.program;
  const stage = entry.stage.stage;
  const grades = entry.stage.grades ?? [];
  const counts = stageCounts(entry.stage);
  const editing = dlg.state?.type === "grade" ? dlg.state.grade : null;

  return (
    <Stack spacing={2.5}>
      <DetailHeader
        kind="Étape"
        crumbs={[{ label: program.name, onClick: () => select({ kind: "program", id: program.id }) }]}
        title={stage.name}
        code={stage.code}
        actions={
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => dlg.show({ type: "stage" })}>
            Modifier
          </Button>
        }
        onDelete={() =>
          void remove({
            entity: "stage",
            label: `l'étape « ${stage.name} »`,
            cascade: cascadeLines(index, { kind: "stage", id: stage.id }),
            chapters: { program_id: program.id, stage_id: stage.id },
            typeToConfirm: stage.code,
            remove: () => deleteStage(stage.id),
            onDone: () => select({ kind: "program", id: program.id }),
          })
        }
        deleteLabel="Supprimer l'étape"
      />

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Identité
        </Typography>
        <Facts
          items={[
            { label: "Code", value: <span className="mono">{stage.code}</span> },
            { label: "Rang", value: stage.sort_order },
            { label: "Niveaux", value: counts.grades },
            { label: "Filières", value: counts.tracks },
            { label: "Offres", value: counts.offerings },
          ]}
        />
      </Paper>

      <Section
        title="Niveaux"
        description="Les années de l'étape, dans l'ordre. Ouvrez un niveau pour gérer ses filières et ses matières."
        count={grades.length}
        addLabel="Ajouter un niveau"
        onAdd={() => dlg.show({ type: "grade", grade: null })}
        flush
      >
        {grades.length === 0 ? (
          <SectionEmpty title="Aucun niveau" description="Ajoutez le premier niveau de cette étape." />
        ) : (
          grades.map((g) => {
            const nTracks = (g.tracks ?? []).length;
            return (
              <ListRow
                key={g.grade.id}
                lead={<Rank n={g.grade.sort_order} />}
                primary={g.grade.name}
                code={g.grade.code}
                secondary={[
                  plural((g.offerings ?? []).length, "matière offerte", "matières offertes", "aucune matière offerte"),
                  nTracks ? plural(nTracks, "filière") : "",
                ]
                  .filter(Boolean)
                  .join(" · ")}
                onOpen={() => select({ kind: "grade", id: g.grade.id })}
                openLabel={`Ouvrir le niveau ${g.grade.name}`}
                onEdit={() => dlg.show({ type: "grade", grade: g.grade })}
                onDelete={() =>
                  void remove({
                    entity: "grade",
                    label: `le niveau « ${g.grade.name} »`,
                    cascade: cascadeLines(index, { kind: "grade", id: g.grade.id }),
                    chapters: { program_id: program.id, grade_id: g.grade.id },
                    typeToConfirm: g.grade.code,
                    remove: () => deleteGrade(g.grade.id),
                  })
                }
              />
            );
          })
        )}
      </Section>

      <SimpleEntityDialog
        open={dlg.isOpen("stage")}
        onClose={dlg.close}
        entity="stage"
        title="Modifier l'étape"
        context={`Programme : ${program.name}`}
        initial={stage}
        siblings={(entry.program.stages ?? []).map((s) => s.stage)}
        orderUnique
        successMessage="Étape enregistrée."
        onSubmit={async (v) => {
          await updateStage(stage.id, { program_id: program.id, code: v.code, name: v.name, sort_order: v.sort_order });
        }}
      />
      <SimpleEntityDialog
        open={dlg.isOpen("grade")}
        onClose={dlg.close}
        entity="grade"
        title={editing ? "Modifier le niveau" : "Nouveau niveau"}
        context={`${program.name} › ${stage.name}`}
        initial={editing}
        siblings={grades.map((g) => g.grade)}
        namePlaceholder="1re année du baccalauréat"
        codePlaceholder="1BAC"
        successMessage={editing ? "Niveau enregistré." : "Niveau créé."}
        onSubmit={async (v) => {
          const payload = { stage_id: stage.id, code: v.code, name: v.name, sort_order: v.sort_order };
          if (editing) await updateGrade(editing.id, payload);
          else await createGrade(payload);
        }}
      />
    </Stack>
  );
};

export default StagePanel;
